import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, LinkIcon, ShoppingBag, BookOpen, MessagesSquare, MessageCircle, TrendingUp, DollarSign, Flame, ArrowUp, ArrowDown } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [costs, setCosts] = useState(null);
  const isAdmin = user?.roles?.some((r) => ["admin", "super_admin"].includes(r));
  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/dashboard/stats").then((r) => setStats(r.data));
      // Pull the platform + per-user cost aggregate (already computed server-side).
      api.get("/admin/user-usage", { params: { days: 1 } }).then((r) => setCosts(r.data)).catch(() => {});
    }
  }, [isAdmin]);
  if (!isAdmin) {
    if (user?.roles?.includes("moderator")) return <Navigate to="/admin/community" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  if (!stats) return <div className="text-center opacity-70 py-12">جاري تحميل الإحصائيات...</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl font-display font-semibold">لوحة الإدارة</h1>
        <p className="opacity-70 mt-2">نظرة عامة على أداء المنصة.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat testid="stat-total" icon={Users} label="إجمالي الأعضاء" value={stats.members.total} />
        <Stat testid="stat-active" icon={Users} label="نشط" value={stats.members.active} />
        <Stat testid="stat-new-week" icon={TrendingUp} label="جدد هذا الأسبوع" value={stats.members.new_week} />
        <Stat testid="stat-new-month" icon={TrendingUp} label="جدد هذا الشهر" value={stats.members.new_month} />
      </div>

      <CostOverviewCard costs={costs} />

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 bg-white border-foreground/10">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="h-4 w-4 text-foreground" />
            <h3 className="font-display font-semibold">روابط التفعيل</h3>
          </div>
          <div className="space-y-1 text-sm">
            <Row label="معلقة" value={stats.invites.pending} />
            <Row label="مستخدمة" value={stats.invites.used} />
            <Row label="منتهية" value={stats.invites.expired} />
          </div>
        </Card>
        <Card className="p-5 bg-white border-foreground/10">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingBag className="h-4 w-4 text-foreground" />
            <h3 className="font-display font-semibold">المشتريات والأنشطة</h3>
          </div>
          <div className="space-y-1 text-sm">
            <Row
              label="عمليات الشراء المدفوعة"
              value={stats.purchases?.paid_total ?? "—"}
              hint="كل عملية دفع تُعدّ منفصلة (حتى لو تكرر العميل)"
            />
            <Row
              label="اشتراكات مدفوعة"
              value={stats.purchases?.paid_subscriptions ?? "—"}
            />
            {stats.purchases?.paid_legacy > 0 && (
              <Row
                label="مشتريات قديمة (غير اشتراك)"
                value={stats.purchases.paid_legacy}
                hint="onetime/installment قبل الترحيل"
              />
            )}
            <Row
              label="عملاء دافعون (فريد)"
              value={stats.purchases?.unique_customers ?? "—"}
              hint="عدد الأشخاص المختلفين"
            />
            {stats.purchases?.pending > 0 && (
              <Row label="بانتظار الدفع" value={stats.purchases.pending} />
            )}
            {stats.purchases?.refunded > 0 && (
              <Row label="مستردة" value={stats.purchases.refunded} />
            )}
            {stats.purchases?.deleted_anonymized > 0 && (
              <Row label="محذوفة (مجهولة)" value={stats.purchases.deleted_anonymized} />
            )}
            <div className="border-t border-foreground/5 mt-2 pt-2">
              <Row label="إجمالي السجلات (خام)" value={stats.purchases?.all_rows ?? stats.purchases_total} />
              <Row label="التسجيلات في الدورات" value={stats.enrollments_total} />
            </div>
          </div>
        </Card>
        <Card className="p-5 bg-white border-foreground/10">
          <div className="flex items-center gap-2 mb-3">
            <MessagesSquare className="h-4 w-4 text-foreground" />
            <h3 className="font-display font-semibold">المجتمع والرسائل</h3>
          </div>
          <div className="space-y-1 text-sm">
            <Row label="منشورات معلقة" value={stats.community.pending_posts} />
            <Row label="أسئلة خاصة" value={stats.community.unanswered_private} />
            <Row label="رسائل غير مقروءة" value={stats.coach.unread_messages} />
          </div>
        </Card>
      </div>

      <Card className="p-5 bg-white border-foreground/10">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-4 w-4 text-foreground" />
          <h3 className="font-display font-semibold">أعلى الدورات تسجيلًا</h3>
        </div>
        {stats.top_courses.length === 0 ? <p className="opacity-60 text-sm">لا بيانات بعد</p> : (
          <div className="space-y-2">
            {stats.top_courses.map((c) => (
              <div key={c.id} className="flex justify-between text-sm py-2 border-b border-foreground/10 last:border-0">
                <span>{c.title}</span>
                <span className="font-mono">{c.count}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ icon: I, label, value, testid }) {
  return (
    <Card className="p-4 bg-white border-foreground/10 text-foreground" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs opacity-70">{label}</p>
          <p className="text-2xl font-display font-semibold mt-1">{value}</p>
        </div>
        <I className="h-4 w-4 text-foreground" />
      </div>
    </Card>
  );
}
function Row({ label, value, hint }) {
  return (
    <div className="flex justify-between items-baseline gap-2">
      <span className="opacity-70">{label}</span>
      <span className="font-mono text-left">
        {value}
        {hint && <div className="text-[10px] opacity-50 font-sans">{hint}</div>}
      </span>
    </div>
  );
}

function CostOverviewCard({ costs }) {
  const totals = (costs && costs.platform_costs) || {};
  const currentMonth = Number(totals.month || 0);
  const lifetime = Number(totals.lifetime || 0);
  const today = Number(totals.today || 0);
  const rows = (costs && costs.users) || [];
  const top = [...rows].sort((a, b) => (b.cost_month_usd || 0) - (a.cost_month_usd || 0)).slice(0, 3);
  const monthLabel = new Date().toLocaleString("ar", { month: "long", year: "numeric" });

  return (
    <Card className="p-5 bg-gradient-to-br from-emerald-50 via-white to-amber-50 dark:from-emerald-950/40 dark:via-transparent dark:to-amber-950/40 border-foreground/10" data-testid="cost-overview-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 grid place-items-center shadow-sm">
            <DollarSign className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-display font-semibold">إجمالي تكلفة الشهر</h3>
            <p className="text-[11px] text-muted-foreground">{monthLabel} — يشمل الذكاء الاصطناعي، RapidAPI، R2، Stream</p>
          </div>
        </div>
        <Link
          to="/admin/user-usage"
          className="text-xs text-primary hover:underline"
          data-testid="link-usage-detail"
        >
          تفاصيل ⟵
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <MoneyStat label="اليوم" value={today} testid="cost-today" />
        <MoneyStat label="الشهر" value={currentMonth} highlight testid="cost-month" />
        <MoneyStat label="منذ البداية" value={lifetime} testid="cost-lifetime" />
      </div>

      {top.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-xs text-muted-foreground">
            <Flame className="h-3.5 w-3.5 text-amber-500" /> أعلى 3 أعضاء استهلاكاً هذا الشهر
          </div>
          <div className="space-y-1.5">
            {top.map((u, i) => (
              <div key={u.user_id} className="flex items-center justify-between text-sm gap-2" data-testid={`top-user-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="text-[10px] w-5 h-5 grid place-items-center p-0">{i + 1}</Badge>
                  <span className="truncate">{u.full_name}</span>
                </div>
                <span className="font-mono text-xs">${Number(u.cost_month_usd || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function MoneyStat({ label, value, highlight, testid }) {
  const v = Number(value || 0);
  return (
    <div className={`rounded-lg p-3 text-center ${highlight ? "bg-emerald-600 text-white shadow-md" : "bg-white/70 dark:bg-foreground/5 border border-foreground/10"}`} data-testid={testid}>
      <div className={`text-[10px] ${highlight ? "text-white/80" : "text-muted-foreground"}`}>{label}</div>
      <div className={`font-mono text-lg sm:text-xl ${highlight ? "" : "text-foreground"}`}>
        ${v.toFixed(2)}
      </div>
    </div>
  );
}
